import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { Organization } from '../../models/organization';
import { OrganizationService } from '../../services/organization.service';
import { OrgUsersAndRoles } from 'src/app/common/user/models/org-users-with-roles';
import { UserService } from 'src/app/common/user/services/user.service';
import {
  SiteMoveExecuteRequest, SiteMoveParentRolePlan, SiteMovePreviewRequest,
  SiteMovePreview, SiteMoveRoleSummary, SiteMoveService, SiteMoveResult, SiteMoveSystem,
} from '../../services/site-move.service';

interface PendingMove {
  operationId: string;
  siteId: string;
  sourceParentId: string;
  targetParentId: string;
}

export interface MoveSiteDialogData {
  organization: Organization;
  parentOrganizations: Organization[];
}

@Component({
  selector: 'app-move-site-dialog',
  templateUrl: './move-site-dialog.component.html',
  styleUrls: ['./move-site-dialog.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, MatAutocompleteModule, MatButtonModule,
    MatDialogModule, MatIconModule, MatInputModule],
})
export class MoveSiteDialogComponent implements OnDestroy {
  // A storage failure must not allow a fresh submission when this tab reopens the dialog.
  private static readonly pendingMoves = new Map<string, PendingMove>();
  readonly organization: Organization;
  readonly parentOrganizations: Organization[];
  readonly currentParentName: string;
  readonly viewerRoleId = 'b11de4b7-3b09-4f1d-9021-36078228da9b';
  filteredParentOrganizations: Organization[];
  readonly destinationSiteCounts = new Map<string, number>();
  siteCountsState: 'loading' | 'loaded' | 'unavailable' = 'loading';
  targetParentId = '';
  targetParentName = '';
  targetOrganizationQuery = '';
  reason = '';
  preview: SiteMovePreview | undefined;
  reviewedRequest: Readonly<SiteMovePreviewRequest> | undefined;
  systems: readonly SiteMoveSystem[] | undefined;
  systemsState: 'not-loaded' | 'loading' | 'loaded' | 'unavailable' = 'not-loaded';
  result: SiteMoveResult | undefined;
  pendingMove: PendingMove | undefined;
  uncertain = false;
  busy = false;
  error = '';
  private readonly pendingStorageKey: string;
  private readonly userDisplayMetadata = new Map<string, string>();
  private systemsRequestId = 0;

  constructor(
    private readonly dialogRef: MatDialogRef<MoveSiteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: MoveSiteDialogData,
    private readonly siteMoveService: SiteMoveService,
    private readonly userService: UserService,
    private readonly organizationService: OrganizationService,
  ) {
    this.organization = data.organization;
    const parents = data.parentOrganizations || [];
    this.currentParentName = this.organization.parentOrg_Name
      || parents.find(parent => parent.organizationId === this.organization.parent_Account_Id)?.name
      || this.organization.parent_Account_Id;
    this.parentOrganizations = parents.filter(parent => parent.enabled && parent.is_Parent_Account
      && parent.organizationId !== this.organization.parent_Account_Id);
    this.filteredParentOrganizations = this.parentOrganizations;
    this.organizationService.getSites().subscribe({
      next: sites => {
        for (const site of sites) {
          if (!site.is_Parent_Account) {
            const parentId = site.parent_Account_Id;
            this.destinationSiteCounts.set(parentId, (this.destinationSiteCounts.get(parentId) || 0) + 1);
          }
        }
        this.siteCountsState = 'loaded';
      },
      error: () => { this.siteCountsState = 'unavailable'; },
    });
    this.pendingStorageKey = `site-move-pending:${this.organization.organizationId}`;
    this.pendingMove = MoveSiteDialogComponent.pendingMoves.get(this.pendingStorageKey);
    try {
      const saved = sessionStorage.getItem(this.pendingStorageKey);
      if (!this.pendingMove && saved) {
        const pending = JSON.parse(saved) as PendingMove;
        if (!pending.operationId || pending.siteId !== this.organization.organizationId
          || !pending.sourceParentId || !pending.targetParentId) {
          throw new Error('Invalid pending move');
        }
        this.pendingMove = pending;
        MoveSiteDialogComponent.pendingMoves.set(this.pendingStorageKey, pending);
      }
      this.uncertain = !!this.pendingMove;
    } catch {
      this.uncertain = true;
      this.error = 'A saved Site Move could not be read. Do not start another move; contact support.';
    }
  }

  ngOnDestroy(): void {
    ++this.systemsRequestId;
  }

  get stage(): 'form' | 'review' | 'uncertain' | 'result' {
    if (this.result) { return 'result'; }
    if (this.uncertain) { return 'uncertain'; }
    return this.preview ? 'review' : 'form';
  }

  get impactReady(): boolean {
    return this.systemsState === 'loaded' && Array.isArray(this.systems)
      && Array.isArray(this.preview?.approvedParentRolePlan)
      && Array.isArray(this.preview?.appliances)
      && !this.preview.appliances.some(appliance => appliance.provisioned);
  }

  onTargetOrganizationInput(event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.targetOrganizationQuery = query;
    if (query !== this.targetParentName) {
      this.targetParentId = '';
      this.targetParentName = '';
    }
    const normalized = query.trim().toLowerCase();
    this.filteredParentOrganizations = this.parentOrganizations.filter(parent =>
      `${parent.name} ${parent.organizationId}`.toLowerCase().includes(normalized));
  }

  selectTargetOrganization(parent: Organization): void {
    this.targetParentId = parent.organizationId;
    this.targetParentName = parent.name;
    this.targetOrganizationQuery = parent.name;
    this.filteredParentOrganizations = this.parentOrganizations;
  }

  displayTargetOrganization(value: Organization | string | null): string {
    return typeof value === 'string' ? value : value?.name || '';
  }

  roleNames(roles: SiteMoveRoleSummary[] | undefined, roleIds: string[]): string[] {
    const names = (roles || []).map(role => role.name).filter(name => !!name);
    if (names.length) { return names; }
    return roleIds?.length ? roleIds : ['None'];
  }

  formatUserDisplayName(plan: SiteMoveParentRolePlan): string {
    return plan.userDisplayName?.trim() || this.userDisplayMetadata.get(plan.userId.toLowerCase())
      || plan.userEmail || plan.userId;
  }

  backToEdit(): void {
    if (this.busy || this.pendingMove) { return; }
    this.preview = undefined;
    this.reviewedRequest = undefined;
    ++this.systemsRequestId;
    this.systems = undefined;
    this.systemsState = 'not-loaded';
    this.error = '';
  }

  previewMove(): void {
    if (this.busy || this.pendingMove || this.uncertain || this.result) { return; }
    if (!this.targetParentId || !this.reason.trim()) { return; }
    this.error = '';
    this.preview = undefined;
    ++this.systemsRequestId;
    this.systems = undefined;
    this.systemsState = 'not-loaded';
    const request: SiteMovePreviewRequest = {
      siteId: this.organization.organizationId, targetParentId: this.targetParentId, reason: this.reason.trim(),
    };
    this.busy = true;
    this.siteMoveService.preview(request).subscribe({
      next: preview => {
        this.preview = JSON.parse(JSON.stringify(preview));
        this.reviewedRequest = Object.freeze({ ...request });
        this.loadSystems();
        this.busy = false;
        if (preview.approvedParentRolePlan?.some(plan => !plan.userDisplayName?.trim())) {
          this.userService.getUsersAndRolesForOrg(this.organization.organizationId, 'External', false).subscribe({
            next: users => (users || []).forEach((user: OrgUsersAndRoles) => {
              const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
              if (user.userId && name) { this.userDisplayMetadata.set(user.userId.toLowerCase(), name); }
            }),
            error: () => { /* Server identity remains available if display metadata is unavailable. */ },
          });
        }
      },
      error: response => {
        this.busy = false;
        this.error = this.formatError(response, 'Reviewing the Site Move failed.');
      },
    });
  }

  private loadSystems(): void {
    const requestId = ++this.systemsRequestId;
    this.systemsState = 'loading';
    this.siteMoveService.getSystems(this.organization.organizationId).subscribe({
      next: systems => {
        if (requestId !== this.systemsRequestId) { return; }
        if (!Array.isArray(systems) || systems.some(system => !system
          || typeof system.assetId !== 'string' || !system.assetId
          || typeof system.assetEnabled !== 'boolean' || typeof system.associationEnabled !== 'boolean')) {
          this.systems = undefined;
          this.systemsState = 'unavailable';
          return;
        }
        this.systems = JSON.parse(JSON.stringify(systems));
        this.systemsState = 'loaded';
      },
      error: () => {
        if (requestId !== this.systemsRequestId) { return; }
        this.systemsState = 'unavailable';
      },
    });
  }

  executeMove(): void {
    const preview = this.preview;
    if (!preview?.canExecute || preview.blockers?.length || !this.impactReady
      || this.busy || this.pendingMove || this.uncertain || this.result) { return; }
    const reviewed = this.reviewedRequest;
    if (reviewed?.siteId !== this.organization.organizationId
      || reviewed.targetParentId !== this.targetParentId || reviewed.reason !== this.reason.trim()
      || preview.siteId !== reviewed.siteId || preview.targetParentId !== reviewed.targetParentId) {
      this.backToEdit();
      this.error = 'The move details changed. Review the Site Move again before confirming.';
      return;
    }
    const request: SiteMoveExecuteRequest = {
      ...reviewed,
      operationId: crypto.randomUUID(),
      expectedSourceParentId: preview.sourceParentId,
      expectedSourceVersion: preview.sourceParentVersion,
      approvedParentRolePlan: JSON.parse(JSON.stringify(preview.approvedParentRolePlan)),
    };
    const pending: PendingMove = {
      operationId: request.operationId, siteId: request.siteId,
      sourceParentId: request.expectedSourceParentId, targetParentId: request.targetParentId,
    };
    try {
      sessionStorage.setItem(this.pendingStorageKey, JSON.stringify(pending));
    } catch {
      this.error = 'The request cannot be safely remembered. Enable browser storage before confirming the Site Move.';
      return;
    }
    this.pendingMove = pending;
    MoveSiteDialogComponent.pendingMoves.set(this.pendingStorageKey, pending);
    // Confirmation requires a complete reviewed count; retain that snapshot in the receipt.
    ++this.systemsRequestId;
    this.error = '';
    this.busy = true;
    this.siteMoveService.execute(request).subscribe({
      next: result => this.acceptResult(result),
      error: response => {
        if (this.matchesPendingResult(response?.error)) {
          this.acceptResult(response.error);
        } else if ([400, 403, 409, 422].includes(response?.status)) {
          this.acceptResult({ ...pending, state: 'Failed',
            errorDetail: this.formatError(response, 'The Site Move was rejected.') });
        } else {
          this.busy = false;
          this.uncertain = true;
          this.error = 'The response is not confirmed. Do not start another move. Check the saved Operation ID.';
        }
      },
    });
  }

  checkStatus(): void {
    if (!this.pendingMove || this.busy) { return; }
    this.busy = true;
    this.error = '';
    this.siteMoveService.getStatus(this.pendingMove.operationId).subscribe({
      next: result => this.acceptResult(result),
      error: response => {
        this.busy = false;
        this.uncertain = true;
        this.error = this.formatError(response, 'Status is not confirmed.')
          + ' A missing status is not proof that the move failed. Keep the original Operation ID; do not start another move.';
      },
    });
  }

  private matchesPendingResult(result: SiteMoveResult | undefined): boolean {
    const pending = this.pendingMove;
    return !!pending && !!result && result.operationId?.toLowerCase() === pending.operationId.toLowerCase()
      && result.siteId?.toLowerCase() === pending.siteId.toLowerCase()
      && result.sourceParentId?.toLowerCase() === pending.sourceParentId.toLowerCase()
      && result.targetParentId?.toLowerCase() === pending.targetParentId.toLowerCase()
      && (result.state === 'Succeeded' || result.state === 'Failed');
  }

  private acceptResult(result: SiteMoveResult): void {
    this.busy = false;
    if (!this.matchesPendingResult(result)) {
      this.uncertain = true;
      this.error = 'The response does not confirm the saved move. Keep the original Operation ID and check status.';
      return;
    }
    this.result = result;
    this.uncertain = false;
    this.pendingMove = undefined;
    MoveSiteDialogComponent.pendingMoves.delete(this.pendingStorageKey);
    try {
      sessionStorage.removeItem(this.pendingStorageKey);
    } catch {
      // A stale saved identity can only trigger a read-only status check when reopened.
    }
  }

  close(): void {
    if (!this.busy) { this.dialogRef.close(this.result?.state === 'Succeeded' ? 'success' : undefined); }
  }

  private formatError(response: any, fallback: string): string {
    const detail = response?.error?.errorDetail || response?.error?.detail || response?.error?.message;
    if (typeof detail === 'string' && detail.trim()) { return detail; }
    if (typeof response?.error === 'string' && response.error.trim()) { return response.error; }
    return response?.status ? `${fallback} (HTTP ${response.status}).` : fallback;
  }
}
